// client/src/hooks/useContratos.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../services/api';
import useAlert from './useAlert';

const CONTRATOS_QUERY_KEY = 'contratos';
const BASE_ROUTE = '/contratos';

// Contrato canônico do payload — ver bloco de documentação em
// server/controllers/contratosController.js (CONTRATO CANÔNICO —
// ContratoPayload). Assinatura digital (assinaturaUrl/dataAssinatura) é
// exclusiva do mobile (PUT /contratos/:id/assinar) — nunca enviar no
// create/update daqui.

// --- HOOKS DE CONSULTA (QUERY) ---
export const useContratos = (params = {}) => {
    const { setAlert } = useAlert();
    return useQuery({
        queryKey: [CONTRATOS_QUERY_KEY, params],
        queryFn: async () => {
            try {
                const { data } = await apiClient.get(BASE_ROUTE, { params });
                return data;
            } catch (error) {
                const errorMsg = error.response?.data?.error || 'Erro ao carregar contratos.';
                setAlert(errorMsg, 'danger');
                throw error;
            }
        },
        placeholderData: (previousData) => previousData,
        staleTime: 1000 * 60 * 5, // 5 minutos
    });
};

export const useContratoById = (id) => {
    const { setAlert } = useAlert();
    return useQuery({
        queryKey: [CONTRATOS_QUERY_KEY, id],
        queryFn: async () => {
            if (!id) return null;
            try {
                const { data } = await apiClient.get(`${BASE_ROUTE}/${id}`);
                return data.data;
            } catch (error) {
                const errorMsg = error.response?.data?.error || 'Erro ao carregar detalhes do contrato.';
                setAlert(errorMsg, 'danger');
                throw error;
            }
        },
        enabled: !!id,
    });
};

// Templates disponíveis por tipo de contrato — ver useTemplatesAtivosPorTipo
// em hooks/useTemplateContrato.js (único hook que faz essa busca; existia
// uma cópia duplicada aqui com cache key diferente, removida).

// --- HOOKS DE AÇÃO (MUTATION) ---
export const useMutateContratos = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();

    const addContrato = useMutation({
        mutationFn: async (contratoData) => {
            try {
                const { data } = await apiClient.post(BASE_ROUTE, contratoData);
                return data;
            } catch (error) {
                const errorMsg = error.response?.data?.error || 'Erro ao criar contrato.';
                throw new Error(errorMsg);
            }
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: [CONTRATOS_QUERY_KEY] });
            setAlert(data.message || 'Contrato criado com sucesso!', 'success');
        },
        onError: (err) => {
            setAlert(err.message || 'Erro ao criar contrato.', 'danger');
        },
    });

    const updateContrato = useMutation({
        mutationFn: async ({ id, contratoData }) => {
            try {
                const { data } = await apiClient.put(`${BASE_ROUTE}/${id}`, contratoData);
                return data;
            } catch (error) {
                const errorMsg = error.response?.data?.error || 'Erro ao atualizar contrato.';
                throw new Error(errorMsg);
            }
        },
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: [CONTRATOS_QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: [CONTRATOS_QUERY_KEY, variables.id] });
            setAlert(data.message || 'Contrato atualizado com sucesso!', 'success');
        },
        onError: (err) => {
            setAlert(err.message || 'Erro ao atualizar contrato.', 'danger');
        },
    });

    const deleteContrato = useMutation({
        mutationFn: async (id) => {
            if (window.confirm('Tem certeza que deseja apagar este contrato?')) {
                try {
                    const { data } = await apiClient.delete(`${BASE_ROUTE}/${id}`);
                    return data;
                } catch (error) {
                    const errorMsg = error.response?.data?.error || 'Erro ao apagar contrato.';
                    throw new Error(errorMsg);
                }
            }
            return Promise.reject(new Error('Ação cancelada'));
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: [CONTRATOS_QUERY_KEY] });
            setAlert(data.message || 'Contrato apagado com sucesso.', 'success');
        },
        onError: (err) => {
            if (err.message !== 'Ação cancelada') {
                setAlert(err.message || 'Erro ao apagar contrato.', 'danger');
            }
        },
    });

    const { isPending: isAdding } = addContrato;
    const { isPending: isUpdating } = updateContrato;
    const { isPending: isDeleting } = deleteContrato;

    return {
        addContrato,
        updateContrato,
        deleteContrato,
        isPending: isAdding || isUpdating || isDeleting
    };
};

// --- HOOKS DE ASSINATURA / RETIRADA ---
// Ver server/controllers/contratosController.js: assinarContrato (papel
// 'contraparte'/'testemunha'/'outro' — 'motorista' é rejeitado, precisa
// passar por confirmarRetiradaContrato) e confirmarRetiradaContrato (ação
// atômica: grava o checklist de itens retirados + a assinatura do motorista
// numa única escrita).
export const useAssinarContrato = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();

    return useMutation({
        mutationFn: async ({ id, assinatura, papel, nomeAssinante, documentoAssinante, observacoes }) => {
            try {
                const { data } = await apiClient.put(`${BASE_ROUTE}/${id}/assinar`, {
                    assinatura, papel, nomeAssinante, documentoAssinante, observacoes
                });
                return data;
            } catch (error) {
                throw new Error(error.response?.data?.error || 'Erro ao assinar contrato.');
            }
        },
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: [CONTRATOS_QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: [CONTRATOS_QUERY_KEY, variables.id] });
            setAlert(data.message || 'Contrato assinado com sucesso!', 'success');
        },
        onError: (err) => setAlert(err.message || 'Erro ao assinar contrato.', 'danger'),
    });
};

export const useCancelarContrato = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();

    return useMutation({
        mutationFn: async ({ id, motivo }) => {
            try {
                const { data } = await apiClient.put(`${BASE_ROUTE}/${id}/cancelar`, { motivo });
                return data;
            } catch (error) {
                throw new Error(error.response?.data?.error || 'Erro ao cancelar contrato.');
            }
        },
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: [CONTRATOS_QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: [CONTRATOS_QUERY_KEY, variables.id] });
            setAlert(data.message || 'Contrato cancelado com sucesso.', 'success');
        },
        onError: (err) => setAlert(err.message || 'Erro ao cancelar contrato.', 'danger'),
    });
};

export const useConfirmarRetirada = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();

    return useMutation({
        mutationFn: async ({ id, motoristaId, veiculoId, itensRetirados, assinatura, observacoes }) => {
            try {
                const { data } = await apiClient.put(`${BASE_ROUTE}/${id}/confirmar-retirada`, {
                    motoristaId, veiculoId, itensRetirados, assinatura, observacoes
                });
                return data;
            } catch (error) {
                throw new Error(error.response?.data?.error || 'Erro ao confirmar retirada.');
            }
        },
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: [CONTRATOS_QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: [CONTRATOS_QUERY_KEY, variables.id] });
            setAlert(data.message || 'Retirada confirmada com sucesso!', 'success');
        },
        onError: (err) => setAlert(err.message || 'Erro ao confirmar retirada.', 'danger'),
    });
};

// --- HOOKS DE ASSINATURA REMOTA (LINK + CÓDIGO) E CONTROLE DE VERSÃO ---
// Ver server/controllers/contratosController.js: gerarLinkAssinaturaContrato
// (mesmo padrão de anamneseController.js::gerarLinkAssinatura) e
// getVersoesContrato (lista Arquivos por referenciaId — cada assinatura/
// retirada já gera um PDF novo, nunca apagado).
export const useGerarLinkAssinaturaContrato = () => {
    const { setAlert } = useAlert();

    return useMutation({
        mutationFn: async (id) => {
            try {
                const { data } = await apiClient.post(`${BASE_ROUTE}/${id}/gerar-link-assinatura`);
                return data.data;
            } catch (error) {
                throw new Error(error.response?.data?.error || 'Erro ao gerar link de assinatura.');
            }
        },
        onError: (err) => setAlert(err.message || 'Erro ao gerar link de assinatura.', 'danger'),
    });
};

export const useVersoesContrato = (id) => {
    const { setAlert } = useAlert();
    return useQuery({
        queryKey: [CONTRATOS_QUERY_KEY, id, 'versoes'],
        queryFn: async () => {
            try {
                const { data } = await apiClient.get(`${BASE_ROUTE}/${id}/versoes`);
                return data.data || [];
            } catch (error) {
                const errorMsg = error.response?.data?.error || 'Erro ao carregar versões do contrato.';
                setAlert(errorMsg, 'danger');
                throw error;
            }
        },
        enabled: !!id,
    });
};